import $$api from 'models/api';
import { useEffect } from 'react';
import { Col, Form, Spinner } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';

import HookFormControl from 'components/HookFormControl';
import CommunityTab from 'components/StyledTemplates/CommunityTab';
import { Button } from 'components/StyledTemplates/StyledTemplates';

import { statesToSkip } from 'constants/common';

import { phoneToBackendFormat, phoneToWidgetFormat } from 'services/Utils';

import useResources from 'hooks/useResources';

import { type TRole } from 'types/Store';
import { type CurrentParticipant } from 'types/zod/current-participant';
import { type Participant } from 'types/zod/participant';

type ProfileEditProps = {
    participant: Participant | CurrentParticipant;
    disallowNameEditing?: boolean;
    role?: TRole;
    onSave?: () => void;
};

const ProfileEdit = (props: ProfileEditProps) => {
    const { participant, disallowNameEditing, role, onSave } = props;

    const { t } = useResources();
    const form = useForm();
    const { formState } = form;
    const { isSubmitting } = formState;

    const generatedOnce = 'generated_once' in participant ? participant.generated_once : false;

    form.watch('address.domicile.state_code');

    useEffect(() => {
        for (const [key, value] of Object.entries(participant)) {
            // @ts-ignore
            form?.setValue(key, key === `phone_number` ? phoneToWidgetFormat(value) : value);
        }
    }, [JSON.stringify(participant)]);

    const useSuggestedAddress = (value: string, item: any) => {
        if (value) {
            form.setValue('address.domicile.address_line1', item.street_line);
            form.setValue('address.domicile.address_line2', item.secondary);
            form.setValue('address.domicile.city', item.city);
            form.setValue('address.domicile.state_code', item.state);
            form.setValue('address.domicile.zip', item.zipcode);
        }
    };

    const handleSubmit = async (event: any) => {
        if (event.phone_number) {
            event.phone_number = phoneToBackendFormat(event.phone_number);
        }
        await $$api.participants.updateParticipantFx(event);
        if (onSave) {
            onSave();
        }
    };

    return (
        <FormProvider {...form}>
            <Form onSubmit={form.handleSubmit(handleSubmit)}>
                <CommunityTab title={t('profile_edit.profile')} extraBody="display: block !important;">
                    {disallowNameEditing && <div className="mb-3 text-secondary">{t('errors:read_only_name')}</div>}
                    <Form.Row>
                        <Form.Group as={Col} sm={6} controlId="first_name">
                            <HookFormControl.Text
                                name="first_name"
                                label={t(`profile_edit.first_name`)}
                                maxLength={60}
                                rules={{ required: t(`profile_edit.first_name_error`) }}
                                disabled={disallowNameEditing || generatedOnce}
                            />
                        </Form.Group>
                        <Form.Group as={Col} sm={6} controlId="middle_name">
                            <HookFormControl.Text
                                name="middle_name"
                                label={t(`profile_edit.middle_name`)}
                                maxLength={60}
                            />
                        </Form.Group>
                    </Form.Row>
                    <Form.Row>
                        <Form.Group as={Col} sm={9} controlId="last_name">
                            <HookFormControl.Text
                                name="last_name"
                                label={t(`profile_edit.last_name`)}
                                maxLength={60}
                                rules={{ required: t(`profile_edit.last_name_error`) }}
                                disabled={disallowNameEditing || generatedOnce}
                            />
                        </Form.Group>
                        <Form.Group as={Col} sm={3} controlId="suffix">
                            <HookFormControl.Suffix name="suffix" label={t(`profile_edit.suffix`)} maxLength={1024} />
                        </Form.Group>
                    </Form.Row>
                    <Form.Row>
                        <Form.Group as={Col} sm={5} controlId="date_of_birth">
                            <HookFormControl.Date
                                name="date_of_birth"
                                label={t(`profile_edit.date_of_birth`)}
                                rules={{ required: t(`profile_edit.date_of_birth_error`) }}
                                hideCalendar={true}
                                className="form-date-input"
                            />
                        </Form.Group>
                        <Form.Group as={Col} sm={5} controlId="phone_number">
                            <HookFormControl.Phone name="phone_number" label={t('profile_edit.phone_number')} />
                        </Form.Group>
                    </Form.Row>
                    {role === 'advisor' && (
                        <Form.Row>
                            <Form.Group as={Col} sm={6} controlId="company">
                                <HookFormControl.Text
                                    name="company"
                                    label={t(`profile_edit.company`)}
                                    maxLength={256}
                                />
                            </Form.Group>
                        </Form.Row>
                    )}
                    <hr />
                    <Form.Row>
                        <Form.Group as={Col} sm={6} controlId="address.domicile.address_line1">
                            <HookFormControl.AddressAutocomplete
                                name="address.domicile.address_line1"
                                label={t(`profile_edit.address_line1`)}
                                rules={{ required: t(`profile_edit.address_line1_error`) }}
                                onSelect={useSuggestedAddress}
                                state={
                                    form.getValues('address.domicile.state_code') ??
                                    form.getValues('domicile_state_code')
                                }
                            />
                        </Form.Group>
                        <Form.Group as={Col} sm={6} controlId="address.domicile.address_line2">
                            <HookFormControl.Text
                                name="address.domicile.address_line2"
                                label={t(`profile_edit.address_line2`)}
                                maxLength={60}
                            />
                        </Form.Group>
                    </Form.Row>
                    <Form.Row>
                        <Form.Group as={Col} sm={4} controlId="address.domicile.city">
                            <HookFormControl.Text
                                name="address.domicile.city"
                                label={t(`profile_edit.city`)}
                                rules={{ required: t(`profile_edit.city_error`) }}
                                maxLength={60}
                            />
                        </Form.Group>
                        <Form.Group as={Col} sm={4} controlId="address.domicile.state_code">
                            <HookFormControl.State
                                name="address.domicile.state_code"
                                statesToSkip={statesToSkip}
                                label={t(`profile_edit.state`)}
                                rules={{ required: t(`profile_edit.state_error`) }}
                            />
                        </Form.Group>
                        <Form.Group as={Col} sm={4} controlId="address.domicile.zip">
                            <HookFormControl.Text
                                name="address.domicile.zip"
                                label={t(`profile_edit.zip`)}
                                pattern="(^\d{5}$)|(^\d{5}-\d{4}$)"
                                type="text"
                                maxLength={10}
                                className="zip"
                                rules={{ required: t(`profile_edit.zip_error`) }}
                            />
                        </Form.Group>
                    </Form.Row>
                    <Form.Row className="d-flex justify-content-end mt-3">
                        <Button data-qaid="save_button" type="submit" disabled={isSubmitting} className="px-5">
                            {isSubmitting ? <Spinner animation="border" size="sm" /> : t('profile_edit.save')}
                        </Button>
                    </Form.Row>
                </CommunityTab>
            </Form>
        </FormProvider>
    );
};

export default ProfileEdit;
